import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import { Button } from '@carbon/react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Draggable,
  Play,
} from '@carbon/icons-react';
import { FormattedCount } from 'components/formatted-count';
import type { FactorGroupRow } from '../../types';
import styles from './factor-order.module.scss';

interface FactorOrderProps {
  /** The factor groups in their current engine order. */
  groups: FactorGroupRow[];
  /** Total changed factors across all groups. */
  totalChanged: number;
  /** Engine iterations (changed factors + 1). */
  iterations: number;
  /** Move the group at `from` to index `to`. */
  onReorder: (from: number, to: number) => void;
  onBack: () => void;
  onRun: () => void;
  /** Disables the Run button while the run is being created (default false). */
  isRunPending?: boolean;
}

interface SortableRowProps {
  group: FactorGroupRow;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

// One draggable factor-group row (drag handle + position + label + count + arrows).
function SortableRow({
  group,
  index,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames(styles.row, { [styles.rowDragging]: isDragging })}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        className={styles.handle}
        aria-label={`Reorder ${group.label}`}
        {...attributes}
        {...listeners}
      >
        <Draggable size={16} className={styles.handleIcon} />
      </button>
      <span className={styles.num}>
        <FormattedCount value={index + 1} />
      </span>
      <span className={styles.label}>{group.label}</span>
      {group.changedCount > 0 ? (
        <span className={styles.changed}>
          <FormattedCount value={group.changedCount} /> factor
          {group.changedCount !== 1 ? 's' : ''} changed
        </span>
      ) : (
        <span className={styles.matched}>No changes</span>
      )}
      <div className={styles.arrows}>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          iconDescription="Move up"
          renderIcon={ArrowUp}
          disabled={isFirst}
          onClick={onMoveUp}
        />
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          iconDescription="Move down"
          renderIcon={ArrowDown}
          disabled={isLast}
          onClick={onMoveDown}
        />
      </div>
    </div>
  );
}

// Step 3 body: a reorderable list of factor groups (drag or arrows) alongside the
// Engine Summary, with a footer to go back or run the comparison engine.
export function FactorOrder({
  groups,
  totalChanged,
  iterations,
  onReorder,
  onBack,
  onRun,
  isRunPending = false,
}: FactorOrderProps) {
  // Pointer drag for mouse/touch; from the keyboard, Space on a row's handle picks it up and the
  // arrow keys move it (as in components/sortable-list) — or use the row's up/down buttons.
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const keys = groups.map((group) => group.key);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = keys.indexOf(String(active.id));
    const to = keys.indexOf(String(over.id));
    if (from !== -1 && to !== -1) onReorder(from, to);
  };

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <div className={styles.list}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={keys}
              strategy={verticalListSortingStrategy}
            >
              {groups.map((group, index) => (
                <SortableRow
                  key={group.key}
                  group={group}
                  index={index}
                  isFirst={index === 0}
                  isLast={index === groups.length - 1}
                  onMoveUp={() => onReorder(index, index - 1)}
                  onMoveDown={() => onReorder(index, index + 1)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        <aside className={styles.summary}>
          <div className={styles.summaryHeader}>Engine Summary</div>
          <div className={styles.summaryBody}>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Factor groups</span>
              <span className={styles.statValue}>
                <FormattedCount value={groups.length} />
              </span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Total changed factors</span>
              <span
                className={classNames(styles.statValue, styles.statNegative)}
              >
                <FormattedCount value={totalChanged} />
              </span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}>Comparison iterations</span>
              <span className={styles.statValue}>
                <FormattedCount value={iterations} />
              </span>
            </div>
            <p className={styles.summaryNote}>
              The engine runs at the slot level and generates one calculation
              per factor change. Results open right away and show the
              engine&apos;s progress until every factor is calculated.
            </p>
          </div>
        </aside>
      </div>

      <div className={styles.footer}>
        <Button kind="ghost" renderIcon={ArrowLeft} onClick={onBack}>
          Back to Analysis
        </Button>
        <div className={styles.footerRight}>
          <span className={styles.footerText}>
            Satisfied with the factor order? Run to start the comparison engine.
          </span>
          <Button
            kind="primary"
            renderIcon={Play}
            onClick={onRun}
            disabled={isRunPending}
          >
            {isRunPending ? 'Starting…' : 'Run Comparison Engine'}
          </Button>
        </div>
      </div>
    </div>
  );
}
